
import Layout from '../components/Layout';
import { MessageSquare, Heart, GamepadIcon, PieChart, Vote, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const Engagement = () => {
  const engagementFeatures = [
    {
      icon: <GamepadIcon className="h-8 w-8 text-fembot-pink" />,
      title: "Rock Paper Scissors",
      description: "Challenge server members to a classic game of rock paper scissors with fun animations and scorekeeping.",
      premium: false
    },
    {
      icon: <Heart className="h-8 w-8 text-fembot-pink" />,
      title: "Interaction Commands",
      description: "Express yourself with cute interaction commands like hug, kiss, snuggle, bite, pat and more with animated responses.",
      premium: false
    },
    {
      icon: <Vote className="h-8 w-8 text-fembot-pink" />,
      title: "Server Polls",
      description: "Create interactive polls for your server to vote on topics, make decisions, or just have fun.",
      premium: true
    },
    {
      icon: <MessageSquare className="h-8 w-8 text-fembot-pink" />,
      title: "Custom Responses",
      description: "Create custom bot responses triggered by specific messages or keywords unique to your server culture.",
      premium: true
    },
    {
      icon: <PieChart className="h-8 w-8 text-fembot-pink" />,
      title: "Server Stats",
      description: "Track engagement metrics and see which features are most popular in your community.",
      premium: true
    }
  ];

  return (
    <Layout>
      <div className="py-24 bg-gradient-to-b from-white to-pink-50 dark:from-gray-800 dark:to-gray-900">
        <div className="container-custom">
          <div className="text-center mb-16">
            <div className="inline-block p-3 rounded-full bg-fembot-pink/10 mb-4">
              <MessageSquare className="h-10 w-10 text-fembot-pink" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 dark:text-white">
              Engagement <span className="text-fembot-pink">Tools</span>
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Keep your Discord server active and entertaining with our fun engagement features
            </p>
            <p className="text-sm text-fembot-pink mt-3 italic">
              Some features are still in development and may not be available at this point in time
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {engagementFeatures.map((feature, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg transition-all hover:shadow-xl hover:-translate-y-1 duration-300 relative overflow-visible">
                {feature.premium && (
                  <div className="absolute -top-3 -right-3 z-10">
                    <div className="inline-flex items-center p-1.5 rounded-full bg-gradient-to-r from-yellow-400 to-amber-600">
                      <div className="bg-white dark:bg-gray-800 rounded-full px-3 py-1">
                        <div className="flex items-center space-x-1 rounded-full text-xs font-semibold text-amber-700 dark:text-amber-400">
                          <Crown className="h-3 w-3" />
                          <span>Premium</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="bg-fembot-pink/10 rounded-full p-3 w-16 h-16 flex items-center justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-2 dark:text-white">{feature.title}</h3>
                <p className="text-gray-600 dark:text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 bg-white dark:bg-gray-800 rounded-xl p-8">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="md:w-1/2">
                <h3 className="text-2xl font-bold mb-4 dark:text-white">Interaction Examples</h3>
                <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-md">
                  <p className="font-mono text-sm mb-2 dark:text-gray-300">/hug @username</p>
                  <p className="font-mono text-sm mb-2 dark:text-gray-300">/kiss @username</p>
                  <p className="font-mono text-sm mb-2 dark:text-gray-300">/snuggle @username</p>
                  <p className="font-mono text-sm mb-2 dark:text-gray-300">/bite @username</p>
                  <p className="font-mono text-sm mb-2 dark:text-gray-300">/pat @username</p>
                  <p className="font-mono text-sm dark:text-gray-300">/rps game</p>
                </div>
              </div>
              <div className="md:w-1/2">
                <h3 className="text-2xl font-bold mb-4 dark:text-white">Premium Engagement Features</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Upgrade to Premium for custom engagement command creation, more interaction types, and server polls.
                </p>
                <Button 
                  className="w-full bg-gradient-to-r from-fembot-pink to-fembot-dark-pink hover:from-fembot-dark-pink hover:to-fembot-pink text-white"
                  asChild
                >
                  <Link to="/premium">Upgrade to Premium</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Engagement;
